import { useSyncExternalStore } from "react";

/**
 * [O2, polecenie właściciela 2026-09-13] ZAKRES „MÓJ DZIAŁ” W ZAPYTANIACH: wybrany dział + zakładka.
 *
 * „należę teraz do 2 działów, nie mam podziału na działy w sekcji /moj-dzial/ — ale jestem dyrektorem tylko w 1 z nich,
 * nie mogę widzieć innych działów (…) a jakbym był przełożonym przełożonych z 2 działów, to widzę ludzi z podziałem
 * i tylko zakładki które ci pisałem że mają być dodane".
 *
 * Hub „Mój Dział" ustawia tu `{ dzialId, zakladka }` (useLayoutEffect — ZANIM strony zakładek wyślą swoje zapytania)
 * i czyści przy odmontowaniu, więc ekrany poza modułem nigdy nie są zawężane. `apiService.cachedGet` woła
 * `dolozZakresDoZapytania` w PIERWSZEJ linii — przed kluczem pamięci podręcznej — więc odpowiedź dla działu A nie
 * sklei się z zapytaniem o dział B.
 *
 * Dlaczego parametry w adresie, a nie nagłówek: klucz sklejania/pamięci liczy się z adresu i `params`, a nagłówek
 * skleiłby odpowiedzi różnych działów; parametr widać też w dzienniku serwera i nie wymaga zmian CORS.
 * Serwer (MyDeptController.ZakresZadaniaAsync) i tak liczy zakres sam — to jest zawężenie, nie autoryzacja.
 */

/** Ścieżki (bez /api), które znają parametry `dzial` i `zakladka`. Nieznany parametr serwer pomija bez szkody. */
export const PREFIKSY_ZAKRESU = [
    "/my-dept/",
    "/goals/team",
    "/career/team",
    "/document-issues/dept",
    "/document-templates/issuable",
    "/calendar/team-schedule",
    "/calendar/schedule/leave-warnings",
    "/calendar/vacation-ranges/pending",
    "/hr-requests/team",
    "/hr-requests/pending",
    "/hr-requests/evaluations",
    "/pomieszczenia/zespol",
    "/delegacje/rozliczenia/zespol",
    "/sejf/zakladki",
];

/** Adresy dopasowywane DOKŁADNIE (bez pod-ścieżek). */
export const ADRESY_DOKLADNE_ZAKRESU = ["/hr-requests"];

/** Nigdy nie zawężane: lista działów przełącznika i zlecenia adresowane do samego wołającego. */
export const WYKLUCZONE_Z_ZAKRESU = ["/my-dept/scope", "/my-dept/assignments"];

const PUSTY = Object.freeze({ dzialId: null, zakladka: null });
let stan = PUSTY;
const sluchacze = new Set();

export const pobierzZakresMojegoDzialu = () => stan;

export const subskrybujZakresMojegoDzialu = (fn) => {
    if (typeof fn !== "function") return () => {};
    sluchacze.add(fn);
    return () => { sluchacze.delete(fn); };
};

export const ustawZakresMojegoDzialu = ({ dzialId = null, zakladka = null } = {}) => {
    const nowyDzial = dzialId ? String(dzialId) : null;
    const nowaZakladka = zakladka ? String(zakladka) : null;
    if (stan.dzialId === nowyDzial && stan.zakladka === nowaZakladka) return;
    stan = nowyDzial || nowaZakladka ? Object.freeze({ dzialId: nowyDzial, zakladka: nowaZakladka }) : PUSTY;
    sluchacze.forEach((fn) => {
        try { fn(stan); } catch { /* jeden słuchacz nie może uciszyć pozostałych */ }
    });
};

export const wyczyscZakresMojegoDzialu = () => ustawZakresMojegoDzialu({});

/** Stan sklepu jako hak React (stała tożsamość obiektu między zmianami). */
export const useZakresMojegoDzialu = () =>
    useSyncExternalStore(subskrybujZakresMojegoDzialu, pobierzZakresMojegoDzialu, pobierzZakresMojegoDzialu);

const sciezkaAdresu = (url) => {
    const bezZapytania = String(url).split("?")[0].split("#")[0];
    // Adres pełny (http…/api/…) — bierzemy część po „/api".
    const poApi = bezZapytania.match(/^[a-z]+:\/\/[^/]+(?:\/api)?(\/.*)$/i);
    return poApi ? poApi[1] : bezZapytania;
};

/**
 * Dokłada `dzial`/`zakladka` do konfiguracji GET, gdy: sklep jest ustawiony, adres należy do zakresu,
 * nie jest wykluczony, a wołający nie podał własnej wartości (w `params` ani w samym adresie).
 * Zwraca NOWY obiekt konfiguracji albo dokładnie ten sam, gdy nie ma czego dokładać.
 */
export const dolozZakresDoZapytania = (url, config = {}, metoda = "get") => {
    const { dzialId, zakladka } = stan;
    if (!dzialId && !zakladka) return config;
    if (typeof url !== "string" || String(metoda).toLowerCase() !== "get") return config;

    const sciezka = sciezkaAdresu(url);
    if (WYKLUCZONE_Z_ZAKRESU.includes(sciezka)) return config;
    const pasuje = ADRESY_DOKLADNE_ZAKRESU.includes(sciezka) || PREFIKSY_ZAKRESU.some((p) => sciezka.startsWith(p));
    if (!pasuje) return config;

    const cfg = config || {};
    const wAdresie = url.includes("?") ? new URLSearchParams(url.slice(url.indexOf("?") + 1)) : null;
    const params = { ...(cfg.params || {}) };
    let zmiana = false;
    if (dzialId && params.dzial === undefined && !(wAdresie && wAdresie.has("dzial"))) { params.dzial = dzialId; zmiana = true; }
    if (zakladka && params.zakladka === undefined && !(wAdresie && wAdresie.has("zakladka"))) { params.zakladka = zakladka; zmiana = true; }
    return zmiana ? { ...cfg, params } : config;
};
